/**
 * Utility functions for the Footer component
 */
import { getId } from '@plone/volto/helpers/Url/Url';
import { FooterLogoProps } from '@designsystem/stories/04-organisms/Footer';
import { LogoData } from './Footer.data';
import { LOGO_ALT_PREFIX, LOGO_FOCUS_POINT } from './Footer.constants';

/**
 * Type guard to check if a value is a string
 */
export const isString = (value: unknown): value is string =>
  typeof value === 'string';

/**
 * Filters and maps logos based on language and university context
 *
 * @param logos - Array of logo data
 * @param language - Current language code
 * @param universityId - Optional university identifier
 * @returns Filtered and mapped array of footer logo props
 */
export const filterAndMapLogos = (
  logos: LogoData[],
  language: string,
  universityId?: string,
): FooterLogoProps[] => {
  return logos
    .filter((logo) => !logo.lang || logo.lang.includes(language))
    .filter(
      (logo) =>
        !logo.university ||
        (universityId && logo.university.includes(universityId)),
    )
    .map((logo) => {
      const title = logo.title ?? 'logo';
      const src = logo.src ?? '';
      // Skip entries without a src
      if (!src) return null;
      return {
        href: logo.href ?? '#',
        title,
        media: {
          src,
          alt: `${LOGO_ALT_PREFIX} ${title}`,
          contain: true,
          focus_point: LOGO_FOCUS_POINT,
        },
      };
    })
    .filter(Boolean) as FooterLogoProps[];
};

/**
 * Extracts university ID from a university object
 *
 * @param university - University content object
 * @returns University ID or undefined
 */
export const getUniversityId = (
  university: { '@id': string } | undefined,
): string | undefined => {
  return university ? getId(university['@id']) : undefined;
};
